import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClipboardCheckIcon } from "lucide-react";
import { useLocation, useNavigate } from "react-router";

import { JobPipelineFiltersAndSort } from "./JobPipelineFiltersAndSort";
import { JobPipelineKanban } from "./kanban/JobPipelineKanban";
import { JobPipelineTable } from "./table/JobPipelineTable";
import { NumberOfCandidatesAndPublished } from "../NumberOfCandidatesAndPublished";
import { Center, Column, ErrorText, LoadingIndicator, Row } from "@/components";
import { ReviewPortalToastStack } from "@/components/application-review/ReviewPortalToastStack";
import { ApplicationReviewV2Modal } from "@/components/application-review-v2/ApplicationReviewV2Modal";
import { CandidateSheetV2 } from "@jobs/candidate-sheet-v2/CandidateSheetV2";
import { CandidacyMessagesSheet } from "@jobs/quo-messages/CandidacyMessagesSheet";
import { KanbanStatusChipDialogHost } from "@jobs/pipeline/shared/KanbanStatusChipDialogHost";
import { ShortlistTakeoverDialog } from "@jobs/pipeline/shared/ShortlistTakeoverDialog";
import { ScheduleCalendar } from "@/components/dashboard/seven-day-schedule/ScheduleCalendar";
import { Button } from "@/components/ui/button";

import { SheetScopeProvider } from "@/contexts/SheetScopeContext";
import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import {
  PausedJobMoveDecision,
  usePausedJobMoveGate,
} from "@/contexts/PausedJobMoveGateContext";
import { useApplicationReviewDecision } from "@/hooks/useApplicationReviewDecision";
import { useReportUnexpectedNull } from "@/hooks/useReportUnexpectedNull";
import { useSelectedResourceIdFromUrl } from "@/hooks/useSelectedResourceIdFromUrl";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useUpdateCandidacy } from "@/hooks/mutations/useUpdateCandidacy";
import { useGetCandidaciesMinimal } from "@/hooks/queries/useGetCandidaciesMinimal";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useGetInterviewsForCalendar } from "@/hooks/queries/useGetInterviewsForCalendar";
import { useGetJobInternalConfig } from "@/hooks/queries/useGetJobInternalConfig";
import { useGetUserSettings } from "@/hooks/queries/useGetUserSettings";
import { useGetCachedCompaniesSummary } from "@/hooks/sets/useGetCachedCompaniesSummary";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useUpdateSetUndoLastCandidacyStatusChange } from "@/hooks/updateSets/useUpdateSetUndoLastCandidacyStatusChange";
import {
  filterCandidacies,
  sortCandidacies,
} from "@/lib/candidacyFiltersAndSorts";
import {
  ALL_USERS_KEY,
  candidacyStatusToLabel,
  defaultPipelineFilterAndSortState,
  isRollbackTransition,
  pipelineStageToDefaultCandidacyStatus,
  STATUS_CHANGE_DIALOG_STATUSES,
} from "@/lib/constants";
import { dashboard } from "@/lib/dashboard";
import { formatting } from "@/lib/formatting";
import { locations } from "@/lib/locations";
import { misc } from "@/lib/misc";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { fireColumnRainConfetti } from "@/lib/confetti";
import { pipeline } from "@/lib/pipeline";
import { screeningAvailability } from "@/lib/screeningAvailability";
import { cn } from "@/lib/utils";
import {
  CandidacySource,
  CandidacyStatus,
  DashboardInterview,
  DashboardReviewState,
  DashboardSelectedUser,
  Job,
  MinimalCandidacy,
  PipelineFilterAndSortState,
  PipelineNavState,
  PipelineSheetType,
  PipelineStage,
  PipelineViewPreference,
  ReviewDecision,
  ReviewPortalToast,
  ReviewPortalToastKind,
  ShortlistTakeover,
} from "@/types";

export const JobPipeline = ({
  className,
  job,
  staffer,
}: {
  className?: string;
  job: Job;
  staffer: DashboardSelectedUser;
}) => {
  /* React Hooks */
  const location = useLocation();
  // useState
  const [filterAndSortState, setFilterAndSortState] =
    useState<PipelineFilterAndSortState>(() => {
      // onlyCommsToSend is set when the user clicked a "Comms to Send" cell in
      // the dashboard table.
      const navState = location.state as PipelineNavState | null;
      if (navState?.onlyCommsToSend) {
        return { ...defaultPipelineFilterAndSortState, onlyCommsToSend: true };
      }
      return defaultPipelineFilterAndSortState;
    });

  return (
    <JobPipelineContent
      className={className}
      job={job}
      staffer={staffer}
      filterAndSortState={filterAndSortState}
      setFilterAndSortState={setFilterAndSortState}
    />
  );
};

const JobPipelineContent = ({
  className,
  job,
  staffer,
  filterAndSortState,
  setFilterAndSortState,
}: {
  className?: string;
  job: Job;
  staffer: DashboardSelectedUser;
  filterAndSortState: PipelineFilterAndSortState;
  setFilterAndSortState: (state: PipelineFilterAndSortState) => void;
}) => {
  /* Database CRUD Operations */
  // Read
  // recognize selected candidacy id from URL
  const {
    selectedResourceId: selectedCandidacyId,
    setSelectedResourceId: setSelectedCandidacyId,
  } = useSelectedResourceIdFromUrl({ paramName: "c" });
  const {
    data: candidaciesWithProfiles = [],
    isPending: isCandidaciesPending,
    isError: isCandidaciesError,
  } = useGetCandidaciesMinimal(
    filterAndSortState.includeRejected ||
      filterAndSortState.includeCouldNotContact ||
      filterAndSortState.includeEmploymentEnded,
  );
  // The sheet owns its own data, so a deep link to a candidate hidden from
  // the list (e.g. rejected) still opens without loading them into the list.
  const { data: selectedCandidacyInfo } = useGetCandidacyInfo({
    candidacyId: selectedCandidacyId ? String(selectedCandidacyId) : "",
  });
  const {
    data: userSettings,
    isPending: isUserSettingsPending,
    isError: isUserSettingsError,
  } = useGetUserSettings();
  const currentUser = useGetCurrentUser();
  const { data: jobInternalConfig } = useGetJobInternalConfig();
  const companies = useGetCachedCompaniesSummary();
  const currentCompany = companies.find((c) => c.id === job.companyId);
  const companyName = currentCompany?.name ?? "Company";
  const {
    data: scheduleInterviews = [],
    isPending: isScheduleInterviewsPending,
    isError: isScheduleInterviewsError,
  } = useGetInterviewsForCalendar({
    userId: staffer === ALL_USERS_KEY ? undefined : staffer,
  });
  const isPending = isCandidaciesPending || isUserSettingsPending;
  const isError = isCandidaciesError || isUserSettingsError;

  // Update
  const { decide: decideApplicationReview } = useApplicationReviewDecision();
  const { mutate: undoLastCandidacyStatusChange } =
    useUpdateSetUndoLastCandidacyStatusChange();
  const { mutate: updateCandidacy } = useUpdateCandidacy();
  const pausedJobMoveGate = usePausedJobMoveGate();

  /* Cache Updates */
  const setCandidacyInAllLists = useSetCandidacyInAllLists();

  const reportUnexpectedNull = useReportUnexpectedNull();
  const successToast = useSuccessToast();

  const viewMeta = userSettings?.pipelineViewPreference
    ? { pipeline_view: userSettings.pipelineViewPreference }
    : {};

  // Block shortlisting if user's calendar hasn't been configured yet
  const isShortlistBlocked = screeningAvailability.blocksShortlist({
    candidacyStatus: CandidacyStatus.APPLIED,
    jobAgentActive: !!jobInternalConfig?.stafferAgentActive,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    availability: userSettings?.screeningAvailability || null,
  });

  /* React Hooks */
  // React Router
  const location = useLocation();
  const navigate = useNavigate();

  const [previousCandidacyId, setPreviousCandidacyId] = useState<string | null>(
    null,
  );
  const [nextCandidacyId, setNextCandidacyId] = useState<string | null>(null);
  const [appliedReviewState, setAppliedReviewState] =
    useState<DashboardReviewState | null>(null);
  const [rejectToasts, setRejectToasts] = useState<ReviewPortalToast[]>([]);
  const [requestedStatusChange, setRequestedStatusChange] = useState<{
    candidacyId: string;
    // Only a kanban drag confirms the move with a toast once the dialog saves.
    isFromDrag: boolean;
    status: CandidacyStatus;
  } | null>(null);
  const [takeover, setTakeover] = useState<ShortlistTakeover | null>(null);

  // Just for confetti
  const appliedRegionRef = useRef<HTMLElement | null>(null);
  const setAppliedRegionRef = useCallback((el: HTMLElement | null) => {
    appliedRegionRef.current = el;
  }, []);

  const [openSheet, setOpenSheet] = useState<PipelineSheetType | null>(() =>
    selectedCandidacyId ? PipelineSheetType.CANDIDATE_SHEET : null,
  );

  // Open sheet when ?c= changes externally (e.g. global search nav).
  useEffect(() => {
    if (selectedCandidacyId && openSheet === null) {
      setOpenSheet(PipelineSheetType.CANDIDATE_SHEET);
    }
  }, [selectedCandidacyId, openSheet]);

  // Strip nav state so back/forward doesn't re-apply the filter.
  useEffect(() => {
    const navState = location.state as PipelineNavState | null;
    if (navState?.onlyCommsToSend) {
      void navigate(location.pathname + location.search, {
        replace: true,
        state: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useContext
  const { userNameMap } = useTaggableUsers();

  // useMemo
  const filteredAndSortedCandidacies = useMemo(
    () =>
      sortCandidacies(
        filterCandidacies(candidaciesWithProfiles, filterAndSortState, staffer),
        filterAndSortState.sortKeyAndOrder,
        userNameMap,
      ),
    [candidaciesWithProfiles, filterAndSortState, staffer, userNameMap],
  );

  // Comms-to-send count for the selected staffer view (owned or unowned
  // candidacies), shown on the quick-filter chip.
  const commsToSendCount = useMemo(
    () =>
      candidaciesWithProfiles.filter(
        (candidacy) =>
          dashboard.candidacyBelongsToView(
            { ownerUserIds: candidacy.owners.map((owner) => owner.userId) },
            staffer,
          ) &&
          dashboard.isCommsToSend(
            candidacy.lastCandidacyStatusChange.toStatus,
            candidacy.contactsAttempted,
            candidacy.nextActionAt,
            new Date(),
          ),
      ).length,
    [candidaciesWithProfiles, staffer],
  );

  // Create a list of candidacies grouped by stage.
  const stageGroups = useMemo(
    () => pipeline.bucketedCandidacies(filteredAndSortedCandidacies),
    [filteredAndSortedCandidacies],
  );

  // Flat list of candidacies for navigation in candidate and messages sheets.
  const flatOrderedCandidacies = useMemo(
    () => stageGroups.flatMap((group) => group.rows),
    [stageGroups],
  );

  // Unique locations across this pipeline's candidacies, country-suffix
  // stripped and sorted. Powers the Location filter submenu. Derived from
  // the unfiltered candidacy set so users can find a location that the
  // current filter would otherwise hide.
  const allLocations = useMemo(
    () =>
      Array.from(
        new Set(
          candidaciesWithProfiles
            .map((c) => c.location)
            .filter((location): location is string => location !== null)
            .map(locations.stripCountrySuffix),
        ),
      ).sort(),
    [candidaciesWithProfiles],
  );

  // Unique sources across this pipeline's candidacies. Powers the Source
  // filter submenu. Same rationale as allLocations — only surface options
  // that could actually match a candidacy on this job.
  const allSources = useMemo(
    () =>
      Array.from(
        new Set(
          candidaciesWithProfiles
            .map((c) => c.source)
            .filter((source): source is CandidacySource => source !== null),
        ),
      ),
    [candidaciesWithProfiles],
  );

  const selectedCandidacy = candidaciesWithProfiles.find(
    (c) => c.id === selectedCandidacyId,
  );
  const selectedUserId =
    selectedCandidacy?.talentId ?? selectedCandidacyInfo?.talentId;
  const selectedStatus =
    selectedCandidacy?.lastCandidacyStatusChange.toStatus ??
    selectedCandidacyInfo?.status;

  /* Helpers */
  const showMovedToast = (
    candidacy: MinimalCandidacy,
    status: CandidacyStatus,
  ) => {
    const candidateName = misc.toTitleCase(
      formatting.fullName(candidacy.firstName, candidacy.lastName, "Candidate"),
    );
    successToast({
      description: `Successfully moved ${candidateName} to ${candidacyStatusToLabel[status]}`,
    });
  };

  /* Handlers */
  const setSelectedByCandidacyId = (candidacyId: string | null) => {
    if (!candidacyId) {
      setPreviousCandidacyId(null);
      setNextCandidacyId(null);
      setSelectedCandidacyId(null);
      return;
    }
    setSelectedCandidacyId(candidacyId);
    const index = flatOrderedCandidacies.findIndex((c) => c.id === candidacyId);
    if (index !== -1) {
      setPreviousCandidacyId(flatOrderedCandidacies[index - 1]?.id ?? null);
      setNextCandidacyId(flatOrderedCandidacies[index + 1]?.id ?? null);
    }
  };

  const handleMoveToNextCandidacy = () => {
    if (!nextCandidacyId) return;
    setSelectedByCandidacyId(nextCandidacyId);
  };

  const handleMoveToPreviousCandidacy = () => {
    if (!previousCandidacyId) return;
    setSelectedByCandidacyId(previousCandidacyId);
  };

  const handleCandidacyClick = (candidacy: MinimalCandidacy) => {
    mixpanelUtils.trackRowClick("Candidacy", MIXPANEL_SCREENS.JOB, viewMeta);
    setSelectedByCandidacyId(candidacy.id);
    setOpenSheet(PipelineSheetType.CANDIDATE_SHEET);
  };

  const handleOpenMessages = (candidacy: MinimalCandidacy) => {
    mixpanelUtils.trackButtonClick(
      "Open candidate messages",
      MIXPANEL_SCREENS.JOB,
      viewMeta,
    );
    setSelectedByCandidacyId(candidacy.id);
    setOpenSheet(PipelineSheetType.MESSAGE_SHEET);
  };

  const handleCloseSheet = () => {
    setSelectedByCandidacyId(null);
  };

  const handleOpenProfile = () => {
    setOpenSheet(PipelineSheetType.CANDIDATE_SHEET);
  };

  const handleScheduleCardClick = (interview: DashboardInterview) => {
    if (!interview.candidacy) return;
    mixpanelUtils.trackButtonClick(
      "Open candidate from schedule",
      MIXPANEL_SCREENS.JOB,
      viewMeta,
    );
    setSelectedByCandidacyId(interview.candidacy.id);
    setOpenSheet(PipelineSheetType.CANDIDATE_SHEET);
  };

  const handleReviewAllApplied = (appliedCandidacyIds: string[]) => {
    if (appliedCandidacyIds.length === 0) {
      // Review all button is hidden when the applied stage has no rows, so if we reach here throw error to Sentry
      reportUnexpectedNull(
        "JobPipeline.handleReviewAllApplied",
        "appliedCandidacyIds",
      );
      return;
    }
    mixpanelUtils.trackButtonClick(
      "Review all applied",
      MIXPANEL_SCREENS.JOB,
      viewMeta,
    );
    setAppliedReviewState({
      candidacyIds: appliedCandidacyIds,
      companyId: job.companyId,
      companyName,
      jobId: job.id,
      jobTitle: job.title,
    });
  };

  const handleReviewApplication = (candidacyId: string) => {
    mixpanelUtils.trackButtonClick(
      "Review application",
      MIXPANEL_SCREENS.JOB,
      viewMeta,
    );
    setAppliedReviewState({
      candidacyIds: [candidacyId],
      companyId: job.companyId,
      companyName,
      jobId: job.id,
      jobTitle: job.title,
    });
  };

  const shortlistCandidacy = (
    candidacy: MinimalCandidacy,
    onSuccess?: () => void,
  ) => {
    decideApplicationReview({
      candidacy,
      decision: ReviewDecision.SHORTLIST,
      screen: MIXPANEL_SCREENS.JOB,
      metadata: viewMeta,
      onSuccess,
    });
  };

  const handleShortlist = (
    candidacy: MinimalCandidacy,
    onSuccess?: () => void,
  ) => {
    // Agent-on job without configured availability can't start the agent.
    if (isShortlistBlocked) return;
    // Shortlisting another staffer's candidacy prompts a takeover choice first.
    const ownerUserId =
      candidacy.owners.length > 0 ? candidacy.owners[0].userId : null;
    if (ownerUserId !== null && ownerUserId !== currentUser?.id) {
      setTakeover({
        candidacyId: candidacy.id,
        ownerUserId,
        onShortlist: () => {
          shortlistCandidacy(candidacy, onSuccess);
        },
      });
      return;
    }
    shortlistCandidacy(candidacy, onSuccess);
  };

  // Kanban drag-and-drop: dropping a card on another bucket moves the candidacy
  // to that bucket's first status, the same default used when adding to it.
  const handleMoveCandidacyToStage = async (
    candidacy: MinimalCandidacy,
    stage: PipelineStage,
  ) => {
    const fromStatus = candidacy.lastCandidacyStatusChange.toStatus;
    const status = pipelineStageToDefaultCandidacyStatus[stage];
    mixpanelUtils.trackDragDrop("Move candidacy stage", MIXPANEL_SCREENS.JOB, {
      ...viewMeta,
      from_status: fromStatus,
      to_status: status,
    });

    // Ahead of every branch below, so a parked job asks to be reactivated
    // before a takeover prompt, a rollback confirm, or an interstitial form.
    const decision = await pausedJobMoveGate?.requestDecision({
      candidacyId: candidacy.id,
      fromStatus,
      jobId: job.id,
      toStatus: status,
    });
    if (decision === PausedJobMoveDecision.CANCEL) return;

    // Shortlisting starts the agent and asks about a takeover when another
    // staffer owns the candidacy — but only when the move is a real review
    // decision, not a drag back into In Communication from further along.
    if (
      fromStatus === CandidacyStatus.APPLIED &&
      status === CandidacyStatus.SHORTLIST
    ) {
      handleShortlist(candidacy, () => {
        showMovedToast(candidacy, status);
      });
      return;
    }

    // Leaving offer or placed tears down a placement, so it always confirms
    // through the rollback dialog before anything is written.
    if (
      isRollbackTransition(fromStatus, status) ||
      STATUS_CHANGE_DIALOG_STATUSES.has(status)
    ) {
      setRequestedStatusChange({
        candidacyId: candidacy.id,
        isFromDrag: true,
        status,
      });
      return;
    }

    updateCandidacy(
      {
        candidacyId: candidacy.id,
        updateCandidacyRequest: { fromStatus, status },
      },
      {
        onSuccess: () => {
          setCandidacyInAllLists({
            candidacyId: candidacy.id,
            lastCandidacyStatusChange: {
              createdAt: new Date().toISOString(),
              fromStatus,
              toStatus: status,
              statusChangeReason: null,
              byAgent: false,
            },
          });
          showMovedToast(candidacy, status);
        },
      },
    );
  };

  const handleReject = (candidacy: MinimalCandidacy) => {
    const candidateName = formatting.fullName(
      candidacy.firstName,
      candidacy.lastName,
      "",
    );
    decideApplicationReview({
      candidacy,
      decision: ReviewDecision.REJECT,
      screen: MIXPANEL_SCREENS.JOB,
      metadata: viewMeta,
      onSuccess: () => {
        setRejectToasts((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            candidacyId: candidacy.id,
            candidateName,
            jobId: job.id,
            kind: ReviewPortalToastKind.REJECT,
          },
        ]);
      },
    });
  };

  // Just handling for confetti
  const handleReviewAllAppliedFinished = () => {
    const el = appliedRegionRef.current;
    if (el) {
      requestAnimationFrame(() => {
        fireColumnRainConfetti(el);
      });
    }
    setAppliedReviewState(null);
  };

  const handleToastDismiss = (toastId: string) => {
    setRejectToasts((prev) => prev.filter((t) => t.id !== toastId));
  };

  const handleToastUndo = (toast: ReviewPortalToast) => {
    mixpanelUtils.trackButtonClick(
      "Undo Reject Candidacy",
      MIXPANEL_SCREENS.JOB,
      viewMeta,
    );
    undoLastCandidacyStatusChange(
      { candidacyId: toast.candidacyId },
      {
        onSuccess: () => {
          handleToastDismiss(toast.id);
        },
      },
    );
  };

  /* Render */
  const renderView = () => {
    if (userSettings?.pipelineViewPreference === PipelineViewPreference.TABLE) {
      return (
        <JobPipelineTable
          hasAnyCandidacies={candidaciesWithProfiles.length > 0}
          isFilteredEmpty={filteredAndSortedCandidacies.length === 0}
          isShortlistBlocked={isShortlistBlocked}
          onCandidacyClick={handleCandidacyClick}
          onOpenMessages={handleOpenMessages}
          onReject={handleReject}
          onRequestStatusChangeDialog={(candidacyId, status) => {
            setRequestedStatusChange({
              candidacyId,
              isFromDrag: false,
              status,
            });
          }}
          onReviewAllApplied={handleReviewAllApplied}
          onShortlist={handleShortlist}
          setAppliedRegionRef={setAppliedRegionRef}
          setSortState={(sortKeyAndOrder) => {
            setFilterAndSortState({
              ...filterAndSortState,
              sortKeyAndOrder,
            });
          }}
          sortState={filterAndSortState.sortKeyAndOrder}
          stageGroups={stageGroups}
        />
      );
    }
    return (
      <JobPipelineKanban
        onCandidacyClick={handleCandidacyClick}
        onMoveCandidacyToStage={(candidacy, stage) => {
          void handleMoveCandidacyToStage(candidacy, stage);
        }}
        onOpenMessages={handleOpenMessages}
        onRequestStatusChangeDialog={(candidacyId, status) => {
          setRequestedStatusChange({ candidacyId, isFromDrag: false, status });
        }}
        onReviewAllApplied={handleReviewAllApplied}
        setAppliedRegionRef={setAppliedRegionRef}
        stageGroups={stageGroups}
      />
    );
  };

  const renderContent = () => {
    if (isPending) {
      return <LoadingIndicator className="absolute inset-0" variant="large" />;
    }
    if (isError) {
      return (
        <Center>
          <ErrorText errorMessage="Error retrieving candidacies" />
        </Center>
      );
    }
    return renderView();
  };

  const renderCandidateSheet = () => {
    if (!selectedCandidacyId || !selectedUserId) return null;
    const candidacyId = String(selectedCandidacyId);

    return (
      <CandidateSheetV2
        candidacyId={candidacyId}
        canMoveToNextCandidacy={!!nextCandidacyId}
        canMoveToPreviousCandidacy={!!previousCandidacyId}
        currentCandidacyIndex={flatOrderedCandidacies.findIndex(
          (c) => c.id === candidacyId,
        )}
        fallbackName={
          selectedCandidacy
            ? formatting.fullName(
                selectedCandidacy.firstName,
                selectedCandidacy.lastName,
                "Candidate",
              )
            : undefined
        }
        handleMoveToNextCandidacy={handleMoveToNextCandidacy}
        handleMoveToPreviousCandidacy={handleMoveToPreviousCandidacy}
        job={job}
        onClose={handleCloseSheet}
        topBarAction={
          selectedStatus === CandidacyStatus.APPLIED ? (
            <Button
              className="h-8 gap-2"
              onClick={() => {
                handleReviewApplication(candidacyId);
              }}
              size="sm"
              type="button"
              variant="outline"
            >
              <ClipboardCheckIcon className="h-4 w-4" />
              Review application
            </Button>
          ) : undefined
        }
        totalCandidaciesCount={flatOrderedCandidacies.length}
        workerId={selectedUserId}
      />
    );
  };

  const render = () => (
    <Column className={cn("min-h-0 flex-1", className)}>
      <Row className="min-h-0 flex-1 gap-4">
        <Column className="min-h-0 min-w-0 flex-1 gap-4">
          <NumberOfCandidatesAndPublished
            job={job}
            numberOfCandidates={filteredAndSortedCandidacies.length}
          />
          <JobPipelineFiltersAndSort
            allLocations={allLocations}
            allSources={allSources}
            commsToSendCount={commsToSendCount}
            companyName={companyName}
            disabled={isPending}
            job={job}
            setState={setFilterAndSortState}
            state={filterAndSortState}
          />
          {renderContent()}
        </Column>
        <ScheduleCalendar
          interviews={scheduleInterviews}
          isPending={isScheduleInterviewsPending}
          isError={isScheduleInterviewsError}
          onSelectCandidacy={handleScheduleCardClick}
        />
      </Row>
      <ReviewPortalToastStack
        onDismiss={handleToastDismiss}
        onUndo={handleToastUndo}
        toasts={rejectToasts}
      />
      {appliedReviewState && (
        <SheetScopeProvider
          candidacyId={appliedReviewState.candidacyIds[0] ?? ""}
          companyId={appliedReviewState.companyId}
          companyName={appliedReviewState.companyName}
          isCompact
          job={job}
          jobId={appliedReviewState.jobId}
        >
          <ApplicationReviewV2Modal
            open
            onAllActioned={handleReviewAllAppliedFinished}
            onOpenChange={(next) => {
              if (!next) setAppliedReviewState(null);
            }}
            state={appliedReviewState}
            userId={currentUser?.id ?? 0}
          />
        </SheetScopeProvider>
      )}
      {openSheet === PipelineSheetType.MESSAGE_SHEET && selectedCandidacy && (
        <CandidacyMessagesSheet
          candidacyId={selectedCandidacy.id}
          canMoveToNextCandidacy={!!nextCandidacyId}
          canMoveToPreviousCandidacy={!!previousCandidacyId}
          currentCandidacyIndex={flatOrderedCandidacies.findIndex(
            (c) => c.id === selectedCandidacy.id,
          )}
          handleMoveToNextCandidacy={handleMoveToNextCandidacy}
          handleMoveToPreviousCandidacy={handleMoveToPreviousCandidacy}
          onClose={handleCloseSheet}
          onOpenProfile={handleOpenProfile}
          totalCandidaciesCount={flatOrderedCandidacies.length}
        />
      )}
      {openSheet === PipelineSheetType.CANDIDATE_SHEET &&
        renderCandidateSheet()}
      {requestedStatusChange ? (
        <SheetScopeProvider
          candidacyId={requestedStatusChange.candidacyId}
          companyId={job.companyId}
          companyName={companyName}
          job={job}
          jobId={job.id}
        >
          <KanbanStatusChipDialogHost
            onClose={() => {
              setRequestedStatusChange(null);
            }}
            onStatusChanged={(status) => {
              if (!requestedStatusChange.isFromDrag) return;
              const candidacy = candidaciesWithProfiles.find(
                (c) => c.id === requestedStatusChange.candidacyId,
              );
              if (candidacy) showMovedToast(candidacy, status);
            }}
            status={requestedStatusChange.status}
          />
        </SheetScopeProvider>
      ) : null}
      {takeover && (
        <ShortlistTakeoverDialog
          {...takeover}
          onClose={() => {
            setTakeover(null);
          }}
        />
      )}
    </Column>
  );

  return render();
};
